import type {
  CommonResponse,
  CommonResponseSliceNoticeParentResponse,
  GetParentNoticeListParams,
  NoticeParentInsertRequest,
  NoticeParentModifyRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { Slice } from '~/schema/slice'
import type { StrapiEnvelope, StrapiQuery } from '~/schema/strapi'

import {
  adaptAcademyNotices,
  adaptBanners,
  adaptFaqs,
  adaptHomeButtonLinks,
  adaptNoticeBoards,
  adaptOnboardingPopups,
  adaptParentsAppNotices,
  adaptReviews,
  adaptServerMaintenance,
  adaptStudentNoticePopups,
  adaptUpdatedContents,
  adaptVodColumns,
} from './adapters'
import type {
  AcademyNotice,
  Banner,
  Faq,
  HomeButtonLinks,
  NoticeBoard,
  OnboardingPopup,
  ParentsAppNotice,
  Review,
  ServerMaintenance,
  StudentNoticePopup,
  UpdatedContent,
  VodColumn,
} from './schemas'

export type { StrapiQuery }

/***************** 학부모앱 공지 (메인 서버) *****************/
export type ParentsAppNoticeListParams = GetParentNoticeListParams
export type ParentsAppNoticeCreateBody = NoticeParentInsertRequest
export type ParentsAppNoticeUpdateBody = NoticeParentModifyRequest

export const getParentsAppNotices = async (
  params: ParentsAppNoticeListParams,
  meta?: RequestMeta,
): Promise<Slice<ParentsAppNotice>> => {
  const body = await httpClient().get<CommonResponseSliceNoticeParentResponse>('/notices/parents', {
    params,
    meta,
  })
  return adaptParentsAppNotices(body.data)
}

export const createParentsAppNotice = async (
  notice: ParentsAppNoticeCreateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CommonResponse>('/notices/parents', notice, { meta })
}

export const updateParentsAppNotice = async (
  id: ParentsAppNotice['id'],
  notice: ParentsAppNoticeUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<CommonResponse>(`/notices/parents/${id}`, notice, { meta })
}

export const deleteParentsAppNotices = async (
  ids: ParentsAppNotice['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>('/notices/parents', { data: ids, meta })
}

/***************** Strapi(hcms) 콘텐츠 *****************/
// 생성 타입이 없어 본문을 unknown 으로 받아 adapter 에 넘긴다. Strapi 는 CommonResponse 봉투 대신
// `{ data, meta }` 로 싸서 낸다.

export const getAcademyNotices = async (
  query: StrapiQuery,
  meta?: RequestMeta,
): Promise<{ content: AcademyNotice[]; last: boolean }> => {
  const body = await httpClient('cms').get<unknown>('/mathflat-notices', { params: query, meta })
  return adaptAcademyNotices(body)
}

export const getFaqs = async (query: StrapiQuery, meta?: RequestMeta): Promise<Faq[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-faqs', {
    params: query,
    meta,
  })
  return adaptFaqs(body.data)
}

export const getNoticeBoards = async (
  query: StrapiQuery,
  meta?: RequestMeta,
): Promise<NoticeBoard[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-notices-board', {
    params: query,
    meta,
  })
  return adaptNoticeBoards(body.data)
}

/** 학생 앱 팝업 공지 */
export const getStudentNoticePopups = async (
  query: StrapiQuery,
  meta?: RequestMeta,
): Promise<StudentNoticePopup[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-student-notices', {
    params: query,
    meta,
  })
  return adaptStudentNoticePopups(body.data)
}

export const getOnboardingPopups = async (query: StrapiQuery): Promise<OnboardingPopup[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-onboarding-popups', {
    params: query,
  })
  return adaptOnboardingPopups(body.data)
}

export const getBanners = async (query: StrapiQuery): Promise<Banner[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-banners', {
    params: query,
  })
  return adaptBanners(body.data)
}

export const getUpdatedContents = async (): Promise<UpdatedContent[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/updated-contents')
  return adaptUpdatedContents(body.data)
}

export const getReviews = async (query: StrapiQuery, meta?: RequestMeta): Promise<Review[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-reviews', {
    params: query,
    meta,
  })
  return adaptReviews(body.data)
}

/** 영상·칼럼 목록. 쪽 크기를 넘기지 않으면 Strapi 기본값(25개)까지만 온다 */
export const getVodColumns = async (
  query: StrapiQuery,
  meta?: RequestMeta,
): Promise<VodColumn[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/vods', {
    params: query,
    meta,
  })
  return adaptVodColumns(body.data)
}

// 링크 컴포넌트 안의 학원 코드별 주소(customUrls)까지 받으려면 두 컴포넌트를 깊게 populate 해야 한다.
const HOME_BUTTON_LINKS_POPULATE = {
  homeButtonLinkMathflat: { populate: '*' },
  homeButtonLinkSchoolflat: { populate: '*' },
}

export const getHomeButtonLinks = async (meta?: RequestMeta): Promise<HomeButtonLinks[]> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/mathflat-home-buttons', {
    params: { populate: HOME_BUTTON_LINKS_POPULATE },
    meta,
  })
  return adaptHomeButtonLinks(body.data)
}

export const getServerMaintenance = async (meta?: RequestMeta): Promise<ServerMaintenance> => {
  const body = await httpClient('cms').get<StrapiEnvelope<unknown>>('/server-maintenance', {
    meta,
  })
  return adaptServerMaintenance(body.data)
}
